import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import {
  getSubscriptionRequests,
  updateSubscriptionStatus,
} from "../services/adminService";

const currencies = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
];

const formatPrice = (price, currencyCode) => {
  const currency = currencies.find((c) => c.code === currencyCode);
  return `${currency ? currency.symbol : "$"}${price}`;
};

const SubscriptionRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [processingId, setProcessingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await getSubscriptionRequests({
        page: currentPage,
        limit: 10,
        status: statusFilter,
      });
      setRequests(data.requests);
      setPagination(data.pagination);
    } catch (error) {
      console.error("Error fetching subscription requests:", error);
      toast.error("Failed to fetch subscription requests");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleApprove = async (subscriptionId) => {
    try {
      setProcessingId(subscriptionId);
      await updateSubscriptionStatus(subscriptionId, "approve");
      toast.success("Subscription approved successfully");
      fetchRequests();
    } catch (error) {
      console.error("Error approving subscription:", error);
      toast.error("Failed to approve subscription");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectClick = (request) => {
    setSelectedRequest(request);
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    try {
      setProcessingId(selectedRequest.id);
      await updateSubscriptionStatus(
        selectedRequest.id,
        "reject",
        rejectionReason
      );
      toast.success("Subscription rejected successfully");
      setShowRejectModal(false);
      setRejectionReason("");
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      console.error("Error rejecting subscription:", error);
      toast.error("Failed to reject subscription");
    } finally {
      setProcessingId(null);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <PageHeader
          backTo="/admin/dashboard"
          title="Subscription Requests"
          subtitle="Review and manage subscription requests"
        />

        {/* Filter Section */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">
              Filter by Status:
            </label>
            <div className="flex space-x-2">
              {[
                { value: "pending", label: "Pending", color: "yellow" },
                { value: "approved", label: "Approved", color: "green" },
                { value: "rejected", label: "Rejected", color: "red" },
                { value: "all", label: "All", color: "gray" },
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => handleStatusFilterChange(filter.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === filter.value
                      ? filter.color === "yellow"
                        ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                        : filter.color === "green"
                        ? "bg-green-100 text-green-800 border border-green-300"
                        : filter.color === "red"
                        ? "bg-red-100 text-red-800 border border-red-300"
                        : "bg-gray-100 text-gray-800 border border-gray-300"
                      : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className="flex-1 text-right text-sm text-gray-500">
              Total: {pagination.total || 0} requests
            </div>
          </div>
        </div>

        {/* Requests Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-12">
              <LoadingSpinner
                size="lg"
                text="Loading subscription requests..."
              />
            </div>
          ) : requests.length === 0 ? (
            <div className="p-12 text-center">
              <svg
                className="w-12 h-12 text-gray-400 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {statusFilter === "all" ? "" : statusFilter} requests found
              </h3>
              <p className="text-gray-600">
                {statusFilter === "all"
                  ? "No subscription requests found."
                  : statusFilter === "pending"
                  ? "No pending requests at this time."
                  : statusFilter === "approved"
                  ? "No approved requests found."
                  : "No rejected requests found."}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Requested
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {requests.map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {request.User?.businessName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.User?.email}
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.User?.phone}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {request.subscriptionPlan?.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatPrice(
                                request.subscriptionPlan?.price,
                                request.subscriptionPlan?.currency
                              )}
                              /{request.subscriptionPlan?.interval}
                            </div>
                            <div className="text-xs text-gray-400">
                              {request.subscriptionPlan?.maxCustomers}{" "}
                              customers, {request.subscriptionPlan?.maxGroups}{" "}
                              groups
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              request.approvalStatus === "pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : request.approvalStatus === "approved"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {request.approvalStatus.charAt(0).toUpperCase() +
                              request.approvalStatus.slice(1)}
                          </span>
                          {request.approvalStatus === "rejected" &&
                            request.rejectionReason && (
                              <div
                                className="text-xs text-gray-500 mt-1"
                                title={request.rejectionReason}
                              >
                                Reason:{" "}
                                {request.rejectionReason.length > 30
                                  ? `${request.rejectionReason.substring(
                                      0,
                                      30
                                    )}...`
                                  : request.rejectionReason}
                              </div>
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            {request.approvalStatus === "pending" ? (
                              <>
                                <button
                                  onClick={() => handleApprove(request.id)}
                                  disabled={processingId === request.id}
                                  className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 transition duration-200"
                                >
                                  {processingId === request.id ? (
                                    <LoadingSpinner size="sm" />
                                  ) : (
                                    "Approve"
                                  )}
                                </button>
                                <button
                                  onClick={() => handleRejectClick(request)}
                                  disabled={processingId === request.id}
                                  className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 transition duration-200"
                                >
                                  Reject
                                </button>
                              </>
                            ) : (
                              <span className="text-gray-400 text-sm">
                                {request.approvalStatus === "approved"
                                  ? "Already approved"
                                  : "Already rejected"}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}{" "}
                    of {pagination.total} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Reject Subscription Request
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this subscription request:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={4}
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason("");
                  setSelectedRequest(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionRequests;
